import { formComponents as fc } from "@/features/i18n/formComponents";
import { createFileRoute } from "@tanstack/react-router";
import { useAuth } from "@/utils/auth";
import { hooks as h } from "@/hooks";
import { ui } from "@patient-watch/ui";
import { AlertDialogCancel } from "@patient-watch/ui/alert-dialog";
import { Badge } from "@patient-watch/ui/badge";
import { Button } from "@patient-watch/ui/button";
import { Switch } from "@patient-watch/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@patient-watch/ui/table";
import {
    type ColumnDef,
    type SortingState,
    flexRender,
    getCoreRowModel,
    getSortedRowModel,
    useReactTable,
} from "@tanstack/react-table";
import { Copy, Loader2, MoreHorizontal, Pencil, Plus, Trash2 } from "lucide-react";
import UnauthorizedPage from "@/components/pages/UnauthorizedPage";
import {
    injectionContentsQueryOptions,
    useInjectionContentsQuery,
} from "@/features/injection/api/selectInjectionContents";
import { useUpdateInjectionContentIsPublic } from "@/features/injection/api/updateInjectionContentIsPublic";
import { useDeleteInjectionContent } from "@/features/injection/api/deleteInjectionContent";
import { useHandleNewInjectionContents } from "@/features/injection/api/handleNewInjectionContents";
import InjectionContentEditForm from "@/features/injection/components/InjectionContentEditForm";
import type { InjectionContentRow as EditFormRow } from "@/features/injection/components/InjectionContentEditForm";
import InjectionContentsForm from "@/features/injection/components/InjectionContentsForm";
import { useMemo, useState } from "react";
import { cn } from "@/lib/utils";

interface InjectionContentRow {
    injection_substance: string;
    dose: number;
    unit: string;
    is_public?: boolean;
}

const InjectionContentsBuilderPage = () => {
    const auth = useAuth();
    const { mutate: updateIsPublic } = useUpdateInjectionContentIsPublic();
    const { mutate: deleteContent } = useDeleteInjectionContent();
    const { mutate: duplicateContent } = useHandleNewInjectionContents();
    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [editingRow, setEditingRow] = useState<InjectionContentRow | null>(null);
    const [deletingRow, setDeletingRow] = useState<InjectionContentRow | null>(null);

    h.useNav([
        { label: "Builder", route: { to: "/builder" } },
        { label: "Injection contents", route: { to: "/builder/injection-contents" } },
    ]);

    const { data: contents, isLoading } = useInjectionContentsQuery();
    const rows = useMemo(
        () =>
            ([...(contents ?? [])] as InjectionContentRow[]).sort(
                (a, b) =>
                    a.injection_substance.localeCompare(b.injection_substance) ||
                    a.dose - b.dose ||
                    a.unit.localeCompare(b.unit)
            ),
        [contents]
    );

    if (!auth?.roles?.isAdmin && !auth?.isSuperUser) {
        return <UnauthorizedPage message="Admin Only Page" />;
    }

    const [sorting, setSorting] = useState<SortingState>([{ id: "injection_substance", desc: false }]);

    const columns: ColumnDef<InjectionContentRow>[] = useMemo(
        () => [
            {
                accessorKey: "injection_substance",
                header: ({ column }) => (
                    <button
                        type="button"
                        className="flex items-center gap-1 font-medium hover:text-foreground"
                        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                    >
                        Substance
                        {{
                            asc: " ↑",
                            desc: " ↓",
                        }[column.getIsSorted() as string] ?? " ↕"}
                    </button>
                ),
            },
            {
                accessorKey: "dose",
                header: ({ column }) => (
                    <button
                        type="button"
                        className="flex items-center gap-1 font-medium hover:text-foreground"
                        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                    >
                        Dose
                        {{
                            asc: " ↑",
                            desc: " ↓",
                        }[column.getIsSorted() as string] ?? " ↕"}
                    </button>
                ),
                cell: ({ row }) => <span className="text-muted-foreground">{String(row.original.dose)}</span>,
            },
            {
                accessorKey: "unit",
                header: ({ column }) => (
                    <button
                        type="button"
                        className="flex items-center gap-1 font-medium hover:text-foreground"
                        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                    >
                        Unit
                        {{
                            asc: " ↑",
                            desc: " ↓",
                        }[column.getIsSorted() as string] ?? " ↕"}
                    </button>
                ),
            },
            {
                accessorKey: "is_public",
                header: "Published",
                cell: ({ row }) => (
                    <div className="flex items-center gap-2">
                        <Switch
                            checked={row.original.is_public === true}
                            onCheckedChange={(checked) =>
                                updateIsPublic({
                                    key: {
                                        injection_substance: row.original.injection_substance,
                                        dose: row.original.dose,
                                        unit: row.original.unit,
                                    },
                                    is_public: checked === true,
                                })
                            }
                        />
                        <Badge variant={row.original.is_public ? "default" : "secondary"} className="text-xs">
                            {row.original.is_public ? "Yes" : "No"}
                        </Badge>
                    </div>
                ),
            },
            {
                id: "actions",
                header: () => <span className="sr-only">Actions</span>,
                cell: ({ row }) => (
                    <ui.DropdownMenu>
                        <ui.DropdownMenuTrigger asChild>
                            <Button type="button" variant="ghost" size="icon" aria-label="Actions">
                                <MoreHorizontal className="h-4 w-4" />
                            </Button>
                        </ui.DropdownMenuTrigger>
                        <ui.DropdownMenuContent align="end">
                            <ui.DropdownMenuItem onClick={() => setEditingRow(row.original)}>
                                <Pencil className="mr-2 h-4 w-4" />
                                Edit
                            </ui.DropdownMenuItem>
                            <ui.DropdownMenuItem
                                onClick={() =>
                                    duplicateContent({
                                        user_id: auth.id,
                                        contents: {
                                            injection_substance: `${row.original.injection_substance} (copy)`,
                                            dose: String(row.original.dose),
                                            unit: row.original.unit,
                                        },
                                    })
                                }
                            >
                                <Copy className="mr-2 h-4 w-4" />
                                Duplicate
                            </ui.DropdownMenuItem>
                            <ui.DropdownMenuItem
                                className="text-destructive focus:text-destructive"
                                onClick={() => setDeletingRow(row.original)}
                            >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Delete
                            </ui.DropdownMenuItem>
                        </ui.DropdownMenuContent>
                    </ui.DropdownMenu>
                ),
            },
        ],
        [updateIsPublic, duplicateContent, auth.id]
    );

    const table = useReactTable({
        data: rows,
        columns,
        state: { sorting },
        onSortingChange: setSorting,
        getRowId: (row) => `${row.injection_substance}::${row.dose}::${row.unit}`,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    if (isLoading) {
        return (
            <div className="flex justify-center items-center py-12">
                <Loader2 className="w-8 h-8 text-muted-foreground animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-6 p-2 md:p-4">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="font-semibold text-3xl tracking-tight">Injection contents</h1>
                    <p className="text-muted-foreground">
                        Manage injection substance/dose/unit options available in diaries.
                    </p>
                </div>
                <ui.Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
                    <ui.DialogTrigger asChild>
                        <Button size="sm">
                            <Plus className="mr-2 w-4 h-4" />
                            Add injection content
                        </Button>
                    </ui.DialogTrigger>
                    <ui.DialogContent>
                        <ui.DialogHeader>
                            <ui.DialogTitle>Add injection content</ui.DialogTitle>
                            <ui.DialogDescription>Add a new substance/dose/unit combination.</ui.DialogDescription>
                        </ui.DialogHeader>
                        <InjectionContentsForm onSuccess={() => setAddDialogOpen(false)} />
                    </ui.DialogContent>
                </ui.Dialog>
            </div>

            <ui.Dialog open={editingRow !== null} onOpenChange={(open) => !open && setEditingRow(null)}>
                <ui.DialogContent>
                    <ui.DialogHeader>
                        <ui.DialogTitle>Edit injection content</ui.DialogTitle>
                        <ui.DialogDescription>
                            Change substance, dose, or unit. This updates the reference tables and the injection content
                            row.
                        </ui.DialogDescription>
                    </ui.DialogHeader>
                    {editingRow && (
                        <InjectionContentEditForm
                            initialValues={editingRow as EditFormRow}
                            onSuccess={() => setEditingRow(null)}
                            onCancel={() => setEditingRow(null)}
                        />
                    )}
                </ui.DialogContent>
            </ui.Dialog>

            <ui.AlertDialog open={deletingRow !== null} onOpenChange={(open) => !open && setDeletingRow(null)}>
                <ui.AlertDialogContent>
                    <ui.AlertDialogHeader>
                        <ui.AlertDialogTitle>Delete injection content?</ui.AlertDialogTitle>
                        <ui.AlertDialogDescription>
                            This will permanently delete{" "}
                            <strong>
                                {deletingRow?.injection_substance} {deletingRow?.dose} {deletingRow?.unit}
                            </strong>
                            . This action cannot be undone.
                        </ui.AlertDialogDescription>
                    </ui.AlertDialogHeader>
                    <ui.AlertDialogFooter>
                        <AlertDialogCancel asChild>
                            <fc.CancelButton size="default" className="w-auto" />
                        </AlertDialogCancel>
                        <ui.AlertDialogAction asChild>
                            <fc.DeleteButton
                                size="default"
                                onClick={() => {
                                    if (deletingRow) {
                                        deleteContent({
                                            injection_substance: deletingRow.injection_substance,
                                            dose: deletingRow.dose,
                                            unit: deletingRow.unit,
                                        });
                                    }
                                    setDeletingRow(null);
                                }}
                                className="w-auto"
                            />
                        </ui.AlertDialogAction>
                    </ui.AlertDialogFooter>
                </ui.AlertDialogContent>
            </ui.AlertDialog>

            <ui.Card>
                <Table>
                    <TableHeader>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <TableHead
                                        key={header.id}
                                        className={cn(
                                            header.column.getCanSort() &&
                                                "cursor-pointer select-none hover:bg-slate-50 dark:hover:bg-slate-800"
                                        )}
                                    >
                                        {flexRender(header.column.columnDef.header, header.getContext())}
                                    </TableHead>
                                ))}
                            </TableRow>
                        ))}
                    </TableHeader>
                    <TableBody>
                        {table.getRowModel().rows?.length ? (
                            table.getRowModel().rows.map((row) => (
                                <TableRow key={row.id}>
                                    {row.getVisibleCells().map((cell) => (
                                        <TableCell key={cell.id}>
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={columns.length} className="py-12 text-muted-foreground text-center">
                                    No injection contents found. Add one to get started.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </ui.Card>
        </div>
    );
};

export const Route = createFileRoute("/_app/builder/injection-contents/")({
    component: InjectionContentsBuilderPage,
    loader: async ({ context }) => {
        await context.queryClient.ensureQueryData(injectionContentsQueryOptions());
    },
});
